/**
 * Кнопка по макету-эталону (btn, btn.primary, btn.subtle). Варианты и размеры
 * прежние: secondary — обычная кнопка макета, ghost — subtle, danger — красная
 * на той же основе (в макете разрушительных кнопок нет), sm — уменьшенные отступы.
 * Тип по умолчанию — button: кнопка действия внутри формы («Добавить условие»,
 * «Удалить») форму не отправляет; отправляющая кнопка объявляет type="submit".
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader } from '@/components/Loader';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	/** Показать спиннер и заблокировать кнопку */
	loading?: boolean;
	/** Растянуть на всю ширину контейнера */
	fullWidth?: boolean;
	children: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
	primary: 'ws-btn ws-primary',
	secondary: 'ws-btn',
	ghost: 'ws-btn ws-subtle',
	danger:
		'ws-btn border-[var(--ws-red)] bg-[var(--ws-red)] text-white hover:border-[var(--ws-red)] hover:bg-[var(--ws-red)] hover:opacity-90',
};

const sizeClasses: Record<ButtonSize, string> = {
	sm: 'min-h-0 px-[10px] py-[5px]',
	md: '',
	lg: '',
};

export function Button({
	type = 'button',
	variant = 'primary',
	size = 'md',
	loading = false,
	fullWidth = false,
	disabled,
	children,
	className = '',
	...rest
}: ButtonProps) {
	const isDisabled = disabled || loading;

	return (
		<button
			type={type}
			className={[
				variantClasses[variant],
				sizeClasses[size],
				'select-none disabled:cursor-not-allowed disabled:opacity-50',
				fullWidth ? 'w-full' : '',
				className,
			]
				.filter(Boolean)
				.join(' ')}
			disabled={isDisabled}
			{...rest}
		>
			{loading && <Loader size={size === 'sm' ? 12 : 14} />}
			{children}
		</button>
	);
}
